import { unitFromAngle } from '../../shared/angle';
import { nextFloat, seedRng } from '../../shared/prng';
import type { Vec2 } from '../../shared/vec';
import type { EdgeSide, ObstacleDefinition, SetDefinition } from '../types';
import { ALLEY_STANDS, type AlleyExtra, FESTIVAL, type TrailExtra, pathLook } from './festival';

// Read by the sim for what blocks and by the render for what it draws: the same numbers on both sides.

const TAU = 2 * Math.PI;
const RANK_MIX = 0x9e3779b9;
const SWAY = { amplitude: 46, period: 2600 };
// The camera never looks past a path's long edges, so the lake comes into the arena.
const SHORE_IN = 210;

export const POLE = { gap: 290, first: 145, out: 128, radius: 10 };
export const GATE = { radius: 237, back: 260, post: 14 };
const LANTERN = { gap: 330, inset: 40, radius: 16 };
export const HOUSE = { shares: [0.22, 0.64], across: 80 };
export const JETTY = { share: 0.52, half: 22, reach: 130 };
export const CAIRNS: readonly (readonly [share: number, out: number, stones: number])[] = [
  [0.33, 92, 4],
  [0.335, 120, 3],
  [0.7, -96, 3],
];
const FOREST = { treeLine: 70, radius: 60, gap: 40 };
const SHORE = { radius: 16, gap: 14, mudWidth: 6 };
const HOUSE_SOLID = { radius: 48, every: 50 };
const JETTY_RAIL = { radius: 8, gap: 10 };

export interface Way {
  readonly length: number;
  readonly width: number;
  readonly down: boolean;
}

export const wayOf = ({ width, height }: { width: number; height: number }): Way =>
  width >= height
    ? { length: width, width: height, down: false }
    : { length: height, width, down: true };

export const at = (way: Way, along: number, across: number): Vec2 =>
  way.down ? { x: across, y: along } : { x: along, y: across };

export const alongOf = (way: Way, point: Vec2): number => (way.down ? point.y : point.x);

export const acrossOf = (way: Way, point: Vec2): number => (way.down ? point.x : point.y);

const sine = (radians: number): number => unitFromAngle(radians).y;

export const trailAt = (way: Way, along: number): number =>
  way.width / 2 + SWAY.amplitude * sine((along / SWAY.period) * TAU);

export const shoreAt = (way: Way, along: number): number =>
  way.width - SHORE_IN + 10 * sine(along / 85) + 20 * sine(along / 700);

export const headingOf = (side: EdgeSide | undefined): 1 | -1 =>
  side === 'left' || side === 'top' ? -1 : 1;

// Neighbouring seeds start the generator alike: scrambled, they draw unlike paths.
function scramble(value: number): number {
  let h = value >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return (h ^ (h >>> 16)) >>> 0;
}

function drawExtras<K extends string>(
  chances: Readonly<Record<K, number>>,
  seed: number,
  rank: number,
  capped: readonly K[] = [],
  cap = Number.POSITIVE_INFINITY,
): K[] {
  const rng = seedRng(scramble(seed ^ Math.imul(rank + 1, RANK_MIX)));
  const shown: K[] = [];
  let kept = 0;
  for (const extra in chances) {
    if (nextFloat(rng) >= chances[extra]) {
      continue;
    }
    if (capped.includes(extra)) {
      if (kept >= cap) {
        continue;
      }
      kept += 1;
    }
    shown.push(extra);
  }
  return shown;
}

export const alleyExtras = (seed: number, rank: number): AlleyExtra[] =>
  drawExtras(FESTIVAL.path.alley, seed, rank, ALLEY_STANDS, FESTIVAL.path.alleyStands);

export const trailExtras = (seed: number, rank: number): TrailExtra[] =>
  drawExtras(FESTIVAL.path.trail, seed, rank);

export interface Place {
  readonly share: number;
  readonly side: -1 | 1;
  readonly out: number;
}

const STAND_PLACES: readonly Place[] = [
  { share: 0.28, side: -1, out: 270 },
  { share: 0.62, side: 1, out: 280 },
];

export function alleyPlace(extra: AlleyExtra, shown: readonly AlleyExtra[]): Place | undefined {
  return STAND_PLACES[shown.indexOf(extra)];
}

// Stands an alley element drawn at the origin, its front toward +y, at its place facing the trail.
export interface Footing {
  readonly spot: Vec2;
  readonly x: Vec2;
  readonly y: Vec2;
}

export function footingOf(way: Way, { share, side, out }: Place): Footing {
  const along = share * way.length;
  const spot = at(way, along, trailAt(way, along) + side * out);
  const front = at(way, 0, -side);
  return { spot, x: { x: front.y, y: -front.x }, y: front };
}

function placed(footing: Footing, x: number, y: number): Vec2 {
  return {
    x: footing.spot.x + x * footing.x.x + y * footing.y.x,
    y: footing.spot.y + x * footing.x.y + y * footing.y.y,
  };
}

const row = (from: number, to: number, step: number, y: number, radius: number) =>
  Array.from({ length: Math.floor((to - from) / step) + 1 }, (_, i) => ({
    x: from + i * step,
    y,
    radius,
  }));

// The yurt and the awning are overhead: the team walks under them.
const ALLEY_SOLIDS: Readonly<Record<AlleyExtra, readonly ObstacleDefinition[]>> = {
  chai: [{ x: 40, y: 132, radius: 48 }],
  bracelets: row(-90, 90, 18, 73, 13),
  crepes: [
    { x: -40, y: -8, radius: 42 },
    { x: 40, y: -8, radius: 42 },
    ...row(-50, 50, 50, 50, 28),
  ],
  thrift: row(-96, 96, 16, -50, 12),
};

export function gateSpot(way: Way, heading: 1 | -1): Vec2 {
  const end = heading > 0 ? way.length - GATE.back : GATE.back;
  return at(way, end, trailAt(way, end));
}

function gatePosts(way: Way, heading: 1 | -1): ObstacleDefinition[] {
  const end = heading > 0 ? way.length - GATE.back : GATE.back;
  const along = end + heading * GATE.radius * 0.35;
  const middle = trailAt(way, end);
  return [-1, 1].map((side) => ({
    ...at(way, along, middle + side * GATE.radius * 0.8),
    radius: GATE.post,
  }));
}

function poles(way: Way): ObstacleDefinition[] {
  const posts: ObstacleDefinition[] = [];
  for (let along = POLE.first; along < way.length; along += POLE.gap) {
    for (const side of [-1, 1]) {
      posts.push({ ...at(way, along, trailAt(way, along) + side * POLE.out), radius: POLE.radius });
    }
  }
  return posts;
}

export const stoneLanterns = (way: Way): Vec2[] => {
  const lamps: Vec2[] = [];
  for (let along = LANTERN.gap / 2; along < way.length; along += LANTERN.gap) {
    lamps.push(at(way, along, shoreAt(way, along) - LANTERN.inset));
  }
  return lamps;
};

export const jettyAlong = (way: Way): number => way.length * JETTY.share;

function shore(way: Way, jetty: boolean): ObstacleDefinition[] {
  const { radius, gap, mudWidth } = SHORE;
  const wall: ObstacleDefinition[] = [];
  const pier = jettyAlong(way);
  for (let along = -gap; along <= way.length + gap; along += gap) {
    if (jetty && Math.abs(along - pier) < JETTY.half + radius) {
      continue;
    }
    wall.push({ ...at(way, along, shoreAt(way, along) + mudWidth + radius), radius });
  }
  if (jetty) {
    const base = shoreAt(way, pier) - 6;
    const rail = JETTY.half + JETTY_RAIL.radius;
    for (let across = base; across <= base + JETTY.reach; across += JETTY_RAIL.gap) {
      for (const side of [-1, 1]) {
        wall.push({ ...at(way, pier + side * rail, across), radius: JETTY_RAIL.radius });
      }
    }
    for (let along = pier - rail; along <= pier + rail; along += JETTY_RAIL.gap) {
      wall.push({
        ...at(way, along, base + JETTY.reach + JETTY_RAIL.radius),
        radius: JETTY_RAIL.radius,
      });
    }
  }
  return wall;
}

function forest(way: Way): ObstacleDefinition[] {
  const trees: ObstacleDefinition[] = [];
  for (let along = 0; along <= way.length; along += FOREST.gap) {
    trees.push({ ...at(way, along, FOREST.treeLine - FOREST.radius), radius: FOREST.radius });
  }
  return trees;
}

function houses(way: Way): ObstacleDefinition[] {
  return HOUSE.shares.flatMap((share) =>
    [-1, 0, 1].map((k) => ({
      ...at(way, share * way.length + k * HOUSE_SOLID.every, HOUSE.across + 4),
      radius: HOUSE_SOLID.radius,
    })),
  );
}

function cairns(way: Way): ObstacleDefinition[] {
  return CAIRNS.map(([share, out]) => {
    const along = way.length * share;
    return { ...at(way, along, trailAt(way, along) + out), radius: LANTERN.radius };
  });
}

export function pathObstacles(
  arena: { width: number; height: number },
  look: 'alley' | 'trail',
  seed: number,
  rank: number,
  heading: 1 | -1,
): ObstacleDefinition[] {
  const way = wayOf(arena);
  const gate = gatePosts(way, heading);
  if (look === 'alley') {
    const shown = alleyExtras(seed, rank);
    return [
      ...poles(way),
      ...gate,
      ...shown.flatMap((extra) => {
        const place = alleyPlace(extra, shown);
        if (place === undefined) {
          return [];
        }
        const footing = footingOf(way, place);
        return ALLEY_SOLIDS[extra].map(({ x, y, radius }) => ({
          ...placed(footing, x, y),
          radius,
        }));
      }),
    ];
  }
  const shown = trailExtras(seed, rank);
  return [
    ...gate,
    ...forest(way),
    ...shore(way, shown.includes('jetty')),
    ...stoneLanterns(way).map((lamp) => ({ ...lamp, radius: LANTERN.radius })),
    ...(shown.includes('houses') ? houses(way) : []),
    ...(shown.includes('cairns') ? cairns(way) : []),
  ];
}

export function pathWithObstacles(
  path: SetDefinition,
  fromId: string | undefined,
  toId: string,
  side: EdgeSide,
  seed: number,
  rank: number,
): SetDefinition {
  return {
    ...path,
    obstacles: pathObstacles(path.arena, pathLook(fromId, toId), seed, rank, headingOf(side)),
  };
}
